
import { Component, Prop, Vue } from 'vue-property-decorator';
import { byName, emptyValue, isAlphabetical, isRecord, isWithin, itemSummary, nullableInner, stringWidget, JsonSchema, ValidationError } from '@/content/schema';
import { changedAt, itemPath } from '@/content/outline';
import { askConfirm } from '@/content/confirm';
import { mediaUrl } from '@/content/pages';

interface Row {
  /** Place in the saved list. */
  index: number;
  /** Number shown in front: the place on screen. */
  position: number;
  title: string;
  detail: string;
  image: string;
  errors: boolean;
  changed: boolean;
}

/** Keys whose text says most about an item after its title, in this order. */
const DETAIL_KEYS = ['role', 'date', 'href', 'url', 'subtitle', 'tag', 'author', 'source', 'definition', 'text', 'description'];

/**
 * A list of groups of fields as rows: drag or arrows to reorder, delete, and a click opens the
 * item on its own (the `open` event, with the item's path). An alphabetical list (the glossary)
 * shows its items by name and cannot be reordered.
 */
@Component
export default class ItemList extends Vue {
  @Prop({ required: true }) readonly schema!: JsonSchema;
  @Prop({ type: Array, required: true }) readonly value!: unknown[];
  @Prop({ required: true }) readonly label!: string;
  @Prop({ required: true }) readonly path!: string;
  @Prop({ type: Array, default: () => [] }) readonly errors!: ValidationError[];
  /** The whole page and its saved version, to mark items with unsaved changes. */
  @Prop({ default: null }) readonly pageValue!: unknown;
  @Prop({ default: null }) readonly savedValue!: unknown;

  mediaUrl = mediaUrl;

  /** Drag and drop: the item whose handle is held, the one being dragged, the one under it. */
  handleDown = -1;
  dragIndex = -1;
  dropIndex = -1;

  get list(): JsonSchema {
    return nullableInner(this.schema) || this.schema;
  }

  get itemSchema(): JsonSchema {
    const items = this.list.items || {};
    return nullableInner(items) || items;
  }

  get alphabetical(): boolean {
    return isAlphabetical(this.schema);
  }

  get help(): string {
    return this.schema.description || '';
  }

  get countLabel(): string {
    const range = this.list.minItems || this.list.maxItems ? ` · ${this.list.minItems || 0}${this.list.maxItems ? ` tot ${this.list.maxItems}` : ' of meer'}` : '';
    return `${this.value.length}${range}`;
  }

  get atMinimum(): boolean {
    return this.list.minItems !== undefined && this.value.length <= this.list.minItems;
  }

  get atMaximum(): boolean {
    return this.list.maxItems !== undefined && this.value.length >= this.list.maxItems;
  }

  get ownErrors(): string[] {
    return this.errors.filter((error) => error.path === this.path).map((error) => error.message.charAt(0).toUpperCase() + error.message.slice(1));
  }

  get hasImages(): boolean {
    return this.rows.some((row) => row.image);
  }

  get rows(): Row[] {
    const rows = this.value.map((item, index) => {
      const path = itemPath(this.path, index);
      return {
        index,
        position: index + 1,
        title: itemSummary(item, index),
        detail: this.detailOf(item),
        image: this.imageOf(item),
        errors: this.errors.some((error) => isWithin(error.path, path)),
        changed: this.savedValue !== null && changedAt(this.pageValue, this.savedValue, path),
      };
    });
    if (!this.alphabetical) return rows;
    return rows.sort((a, b) => byName(a.title, b.title)).map((row, place) => ({ ...row, position: place + 1 }));
  }

  /** The first picture of the item, as a thumbnail. */
  imageOf(item: unknown): string {
    if (!isRecord(item)) return '';
    for (const [key, property] of Object.entries(this.itemSchema.properties || {})) {
      const value = item[key];
      if (typeof value === 'string' && value && stringWidget(key, property, value) === 'image') return value;
    }
    return '';
  }

  /** A second line: what tells this item apart besides its title. */
  detailOf(item: unknown): string {
    if (!isRecord(item)) return '';
    const title = itemSummary(item, 0);
    for (const key of DETAIL_KEYS) {
      const value = item[key];
      if (typeof value === 'string' && value.trim() && value !== title) return value.replace(/\*\*/g, '');
    }
    return '';
  }

  open(index: number): void {
    this.$emit('open', itemPath(this.path, index));
  }

  addItem(): void {
    const index = this.value.length;
    this.$emit('input', [...this.value, emptyValue(this.itemSchema)]);
    // The new item opens, so it can be filled in right away.
    this.$nextTick(() => this.open(index));
  }

  async removeItem(index: number): Promise<void> {
    const item = this.value[index];
    // An item that was never filled in goes without asking.
    const untouched = JSON.stringify(item) === JSON.stringify(emptyValue(this.itemSchema));
    if (!untouched && !(await askConfirm({ title: `"${itemSummary(item, index)}" verwijderen?`, text: 'Het verdwijnt pas van de site nadat je opslaat.', confirmLabel: 'Verwijderen', danger: true }))) return;
    this.$emit('input', this.value.filter((_, i) => i !== index));
  }

  moveItemTo(from: number, to: number): void {
    if (from === to || to < 0 || to >= this.value.length) return;
    const items = [...this.value];
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);
    this.$emit('input', items);
  }

  dropClass(index: number): Record<string, boolean> {
    return {
      'is-dragging': this.dragIndex === index,
      'drop-before': this.dropIndex === index && this.dragIndex > index,
      'drop-after': this.dropIndex === index && this.dragIndex >= 0 && this.dragIndex < index,
    };
  }

  onDragStart(index: number, event: DragEvent): void {
    // Only a drag that starts on the handle moves an item.
    if (this.handleDown !== index) return;
    this.dragIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(index));
    }
  }

  onDragOver(index: number, event: DragEvent): void {
    if (this.dragIndex < 0) return;
    event.preventDefault();
    this.dropIndex = index;
  }

  onDrop(index: number, event: DragEvent): void {
    if (this.dragIndex < 0) return;
    event.preventDefault();
    this.moveItemTo(this.dragIndex, index);
    this.onDragEnd();
  }

  onDragEnd(): void {
    this.handleDown = -1;
    this.dragIndex = -1;
    this.dropIndex = -1;
  }
}
